import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import {
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { SelectItem } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { TranslatePipe } from '@ngx-translate/core';
import {
  getDirtyValues,
  toSelectItems,
} from 'src/app/core/functions/common-functions';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { CommercialOutage } from 'src/app/model/commercial-services/commercial-outage';
import {
  CommercialFacilitySearch,
  CommercialFacilitySortOrQuery,
} from 'src/app/model/commercial-services/commercial-facility';
import { CommercialOutageUpdateDto } from 'src/app/model/commercial-services/commercial-outage-update-dto';
import { NEW_COMMERCIAL_OUTAGE } from 'src/app/constants/commercial-constants';
import { CommercialOutageApiService } from 'src/app/services/api/commercial-outage-api.service';
import {
  CommercialOutageMessageKeys,
  ValidationMessageEntry,
} from 'src/app/core/type-helpers/validation-msg';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { CommercialOutageBuilderService } from 'src/app/services/builder/commercial-outage-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { CommercialOutageFormComponent } from '../../components/commercial-outage-form/commercial-outage-form.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';

@Component({
  selector: 'app-commercial-outage-detail-container',
  templateUrl: './commercial-outage-detail-container.component.html',
  styleUrls: ['./commercial-outage-detail-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    ButtonDirective,
    ScrollPanel,
    CommercialOutageFormComponent,
    TranslatePipe,
    DatePipe,
    BlockableDiv,
  ],
})
export class CommercialOutageDetailContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _fb = inject(UntypedFormBuilder);
  protected _commercialFacilityApi = inject(CommercialFacilityApiService);
  protected _outageApi = inject(CommercialOutageApiService);
  protected _outageBuilder = inject(CommercialOutageBuilderService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _authApi = inject(AuthApiService);

  readonly outage = signal<CommercialOutage | null>(null);
  readonly isLoading = signal(true);
  readonly saving = signal(false);
  readonly formDirty = signal(false);
  readonly canCreate = signal(false);
  readonly canEdit = signal(false);
  readonly formState = signal<UntypedFormGroup | undefined>(undefined);
  readonly availableFacilities = signal<SelectItem[]>([]);
  readonly availableMarketAreas = signal<SelectItem[]>([]);
  readonly availableOperatingAreas = signal<SelectItem[]>([]);
  readonly aorOptions = signal<SelectItem[]>([]);
  private facilitiesSub?: Subscription;
  private dependentOptionsSub?: Subscription;
  private facilitySelectionSub?: Subscription;
  validationMessages?: Record<
    CommercialOutageMessageKeys,
    ValidationMessageEntry
  >;
  ngOnInit(): void {
    this.setupTabClosingSubscription();
    this.initialize();
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
    this.tabClosingSub?.unsubscribe();
    this.facilitiesSub?.unsubscribe();
    this.dependentOptionsSub?.unsubscribe();
    this.facilitySelectionSub?.unsubscribe();
  }

  initialize(): void {
    const validations =
      this._outageBuilder.buildCommercialOutageValidationMessages();
    this.validationMessages = validations.messages;

    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_OUTAGES.CREATE_COMMERCIAL_OUTAGES
      )
    );
    this.canEdit.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_OUTAGES.EDIT_COMMERCIAL_OUTAGES
      )
    );
    this.loadLookupData();
  }

  private loadLookupData(): void {
    forkJoin({
      aors: this._aorApi.getAORs(null),
      operatingAreas: this._commercialFacilityApi.getCommercialOperatingAreas(),
    }).subscribe({
      next: ({ aors, operatingAreas }) => {
        const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
        this.aorOptions.set(toSelectItems(aors, choose));
        this.availableOperatingAreas.set(toSelectItems(operatingAreas, choose));
        this.loadOutage();
      },
      error: (error: unknown) => this.handleLoadError(error),
    });
  }

  private queryOutageFacilityOptions(
    filters: CommercialFacilitySortOrQuery
  ): Observable<CommercialFacilitySearch[]> {
    return this._commercialFacilityApi
      .queryForCommercialFacilities(999999, 1, filters, { facilityName: 'ASC' })
      .pipe(map((connection) => connection.items));
  }

  private loadOutage(): void {
    if (this.id && this.id !== NEW_COMMERCIAL_OUTAGE) {
      this._outageApi.getCommercialOutage(this.id).subscribe({
        next: (outage) => {
          if (!outage) {
            this.handleLoadError(new Error('Commercial outage was not found.'));
            return;
          }
          this.outage.set(outage);
          this.setupScreen();
        },
        error: (error) => this.handleLoadError(error),
      });
      return;
    }
    this.outage.set(this._outageBuilder.buildNewCommercialOutage());
    this._tabService.updateActiveTabLabel(
      this._translateService.instant(
        'COMMERCIAL_SERVICES.LABEL.NEW_COMMERCIAL_OUTAGE'
      )
    );
    this.buildForm();
    this.loadDependentOptions();
  }

  private setupScreen(): void {
    const outage = this.outage();
    if (!outage) {
      this.handleLoadError(new Error('Commercial outage object is null'));
      return;
    }
    this.setTabLabel(outage);
    this.buildForm();
    this.loadDependentOptions();
  }

  private setTabLabel(outage: CommercialOutage): void {
    const tab = this._deviceService.isMobile()
      ? this._tabService.getMobileTab()
      : this._tabService.getTabById(this.id);
    if (!tab || tab.id !== this.id) {
      return;
    }
    const label = this._translateService.instant(
      'COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_OUTAGE'
    );
    const header = `${label}: ${outage.CommercialFacility.facilityName}`;
    if (this._deviceService.isMobile()) {
      tab.header = header;
    } else {
      this._tabService.updateTabLabel(tab, header);
    }
  }

  buildForm(): void {
    const outage = this.outage();
    if (!outage) {
      return;
    }
    const form = this._outageBuilder.buildForm(this._fb, outage);
    this.form = form;
    this.formState.set(form);
    const writeAccess = outage.id ? this.canEdit() : this.canCreate();
    if (!writeAccess) {
      form.disable();
    }
    this.formDirty.set(form.dirty);

    this.valueSub = form.valueChanges.subscribe(() => {
      this.formDirty.set(form.dirty);
      if (form.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  onOperatingAreaChange(operatingAreaId: string | null): void {
    this.facilitySelectionSub?.unsubscribe();
    this.form?.get('commercialMarketAreaId')?.reset(null, {
      emitEvent: false,
    });
    this.loadDependentOptions(operatingAreaId);
  }

  onFacilityFiltersChanged(): void {
    this.facilitySelectionSub?.unsubscribe();
    this.dependentOptionsSub?.unsubscribe();
    this.loadFilteredFacilities();
  }

  onFacilitySelected(facilityId: string | null): void {
    this.facilitySelectionSub?.unsubscribe();
    this.facilitiesSub?.unsubscribe();
    this.dependentOptionsSub?.unsubscribe();
    if (!facilityId) {
      this.form?.patchValue({
        aorId: null,
        commercialOperatingAreaId: null,
        commercialMarketAreaId: null,
      });
      this.availableMarketAreas.set(
        toSelectItems([], this._translateService.instant('COMMON.LABEL.CHOOSE'))
      );
      this.loadFilteredFacilities();
      this.isLoading.set(false);
      return;
    }
    this.isLoading.set(true);
    this.facilitySelectionSub = this._commercialFacilityApi
      .getCommercialFacility(facilityId)
      .pipe(
        switchMap((facility) => {
          if (!facility) {
            throw new Error('Selected commercial facility was not found.');
          }
          const operatingAreaId = facility.operatingArea?.id ?? null;
          return forkJoin({
            facility: of(facility),
            marketAreas: operatingAreaId
              ? this._commercialFacilityApi.getCommercialMarketAreas(
                  operatingAreaId
                )
              : of([]),
          });
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ({ facility, marketAreas }) => {
          this.form?.patchValue({
            aorId: facility.aorId,
            commercialOperatingAreaId: facility.operatingArea?.id ?? null,
            commercialMarketAreaId: facility.marketAreaId,
          });
          this.availableMarketAreas.set(
            toSelectItems(
              marketAreas,
              this._translateService.instant('COMMON.LABEL.CHOOSE')
            )
          );
          this.loadFilteredFacilities();
          this.isLoading.set(false);
        },
        error: (error: unknown) => this.handleLoadError(error),
      });
  }

  private loadDependentOptions(
    operatingAreaId = this.form?.get('commercialOperatingAreaId')?.value ?? null
  ): void {
    this.facilitiesSub?.unsubscribe();
    this.dependentOptionsSub?.unsubscribe();
    this.dependentOptionsSub = forkJoin({
      marketAreas: operatingAreaId
        ? this._commercialFacilityApi.getCommercialMarketAreas(operatingAreaId)
        : of([]),
      facilities: this.queryOutageFacilityOptions(this.getFacilityFilters()),
    }).subscribe({
      next: ({ marketAreas, facilities }) => {
        const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
        this.availableMarketAreas.set(toSelectItems(marketAreas, choose));
        this.setAvailableFacilities(facilities, choose);
        this.isLoading.set(false);
      },
      error: (error: unknown) => this.handleLoadError(error),
    });
  }

  private loadFilteredFacilities(): void {
    this.facilitiesSub?.unsubscribe();
    this.facilitiesSub = this.queryOutageFacilityOptions(
      this.getFacilityFilters()
    ).subscribe({
      next: (facilities) => {
        const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
        this.setAvailableFacilities(facilities, choose);
      },
      error: (error: unknown) => this.handleLoadError(error),
    });
  }

  private getFacilityFilters(): CommercialFacilitySortOrQuery {
    const form = this.form;
    const filters: CommercialFacilitySortOrQuery = {};
    const aorId = form?.get('aorId')?.value;
    const operatingAreaId = form?.get('commercialOperatingAreaId')?.value;
    const marketAreaId = form?.get('commercialMarketAreaId')?.value;
    if (aorId) filters.aor = aorId;
    if (operatingAreaId) filters.operatingArea = operatingAreaId;
    if (marketAreaId) filters.marketArea = marketAreaId;
    return filters;
  }

  private setAvailableFacilities(
    facilities: CommercialFacilitySearch[],
    choose: string
  ): void {
    const facilityControl = this.form?.get('commercialFacilityId');
    const selectedId = facilityControl?.value;
    const facilityMatches = facilities.some(
      (facility) => String(facility.id) === String(selectedId)
    );
    if (selectedId && !facilityMatches) {
      facilityControl?.reset(null, { emitEvent: false });
    }
    this.availableFacilities.set([
      { label: choose, value: null },
      ...facilities.map((facility) => ({
        label: facility.facilityName,
        value: String(facility.id),
      })),
    ]);
  }

  saveOutage(): void {
    const outage = this.outage();
    if (!outage || (outage.id ? !this.canEdit() : !this.canCreate())) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_HEADER',
        bodyKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_BODY',
        bodyParams: {
          action: this._translateService
            .instant('COMMON.LABEL.BUTTONS.SAVE')
            .toLowerCase(),
        },
      });
      return;
    }
    if (!this.form?.valid) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'COMMON.MESSAGES.ERROR.INVALID_FORM',
      });
      return;
    }
    this.saving.set(true);
    const raw = this.form.getRawValue();
    if (!raw.id) {
      this.createOutage(this._outageBuilder.buildCreateObject(raw));
      return;
    }
    this.updateOutage(
      this._outageBuilder.buildUpdateStatement(
        outage,
        getDirtyValues(this.form)
      )
    );
  }

  createOutage(input: CommercialOutageUpdateDto): void {
    this._outageApi.createCommercialOutage(input).subscribe({
      next: (saved) => {
        this.outage.set(saved);
        this._outageBuilder.updateForm(this.form!, saved, this.canEdit());
      },
      error: (error: unknown) => {
        console.error(error);
        this.saving.set(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'COMMON.MESSAGES.ERROR.UPDATE_FAILED_MESSAGE',
        });
      },
      complete: () => {
        const outage = this.outage()!;
        this._tabService.updateTabId(this.id, outage.id);
        this.id = outage.id;
        this.setTabLabel(outage);
        this.finishSave();
      },
    });
  }

  updateOutage(update: CommercialOutageUpdateDto): void {
    const outage = this.outage();
    if (!outage || !this.canEdit()) {
      this.saving.set(false);
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_HEADER',
        bodyKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_BODY',
        bodyParams: {
          action: this._translateService
            .instant('COMMON.LABEL.BUTTONS.SAVE')
            .toLowerCase(),
        },
      });
      return;
    }
    this._outageApi.updateCommercialOutage(outage.id, update).subscribe({
      next: (saved) => {
        this.outage.set(saved);
        this._outageBuilder.updateForm(this.form!, saved, this.canEdit());
        this.setTabLabel(saved);
      },
      error: (error) => {
        console.error(error);
        this.saving.set(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'COMMON.MESSAGES.ERROR.UPDATE_FAILED_MESSAGE',
        });
      },
      complete: () => this.finishSave(),
    });
  }

  private finishSave(): void {
    this.formDirty.set(false);
    this.saving.set(false);
    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });
  }

  private handleLoadError(error: unknown): void {
    console.error(error);
    this.isLoading.set(false);
    this._logAndMessage.translateToErrorAlert({
      headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      bodyKey: 'COMMERCIAL_SERVICES.MESSAGES.ERROR.LOAD_COMMERCIAL_OUTAGES',
    });
  }
}
